import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type {
	AIResponseDTO,
	CreateDocumentBody,
	DocumentDTO,
	DocumentGrantCandidateDTO,
	DocumentType,
	DocumentUploadResponse,
	RealtimeMessage,
} from "@zplanem/responses"
import { useCallback } from "react"
import { apiFetch, uploadToGcs } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"
import {
	documentResourcePath,
	projectBasePath,
	propertyBasePath,
} from "./document-api-paths"
import { usePropertyChannel } from "./use-property-channel"

/** Ready documents for a project. */
export function useProjectDocuments(propertyId: string, projectId: string) {
	return useQuery({
		queryKey: queryKeys.projectDocuments(propertyId, projectId),
		queryFn: () =>
			apiFetch<{ documents: DocumentDTO[] }>(
				projectBasePath(propertyId, projectId),
			).then((r) => r.documents),
		enabled: !!propertyId && !!projectId,
	})
}

/** All documents visible on a property (rollup). */
export function usePropertyDocuments(propertyId: string) {
	return useQuery({
		queryKey: queryKeys.propertyDocuments(propertyId),
		queryFn: () =>
			apiFetch<{ documents: DocumentDTO[] }>(propertyBasePath(propertyId)).then(
				(r) => r.documents,
			),
		enabled: !!propertyId,
	})
}

/** Grant candidates for a document (scope members + granted flag). */
export function useGrantCandidates(propertyId: string, documentId: string) {
	return useQuery({
		queryKey: queryKeys.documentGrantCandidates(propertyId, documentId),
		queryFn: () =>
			apiFetch<{ candidates: DocumentGrantCandidateDTO[] }>(
				`${documentResourcePath(propertyId, documentId)}/grant-candidates`,
			).then((r) => r.candidates),
		enabled: !!propertyId && !!documentId,
	})
}

export type DocumentScope =
	| { type: "project"; propertyId: string; projectId: string }
	| { type: "property"; propertyId: string }

function invalidationKeys(
	qc: ReturnType<typeof useQueryClient>,
	scope: DocumentScope,
) {
	if (scope.type === "project") {
		return qc.invalidateQueries({
			queryKey: queryKeys.projectDocuments(scope.propertyId, scope.projectId),
		})
	}
	return qc.invalidateQueries({
		queryKey: queryKeys.propertyDocuments(scope.propertyId),
	})
}

/** Three-step upload: create → PUT to GCS → confirm. */
export function useUploadDocument(scope: DocumentScope) {
	const qc = useQueryClient()
	const basePath =
		scope.type === "project"
			? projectBasePath(scope.propertyId, scope.projectId)
			: propertyBasePath(scope.propertyId)

	return useMutation({
		mutationFn: async (input: {
			file: File
			title: string
			aiReadable: boolean
			documentType: DocumentType
		}) => {
			const body: CreateDocumentBody = {
				title: input.title,
				contentType: input.file.type,
				filename: input.file.name,
				declaredSize: input.file.size,
				documentType: input.documentType,
				aiReadable: input.aiReadable,
			}
			const created = await apiFetch<DocumentUploadResponse>(basePath, {
				method: "POST",
				body: JSON.stringify(body),
			})
			await uploadToGcs(created.upload, input.file)
			// Create + list are scope-specific (basePath), but every id-addressed
			// op — confirm, download, update, delete, grants — is registered only
			// under the property path (a document id is unique within a property).
			// Building confirm off basePath 404s for project-scoped uploads.
			return apiFetch<DocumentDTO>(
				`${documentResourcePath(scope.propertyId, created.document.id)}/confirm`,
				{
					method: "POST",
				},
			)
		},
		onSuccess: () => invalidationKeys(qc, scope),
	})
}

/** Delete a document. */
export function useDeleteDocument(scope: DocumentScope) {
	const qc = useQueryClient()
	const propertyId = scope.propertyId
	return useMutation({
		mutationFn: (documentId: string) =>
			apiFetch<{ deleted: true }>(
				documentResourcePath(propertyId, documentId),
				{
					method: "DELETE",
				},
			),
		onSuccess: () => invalidationKeys(qc, scope),
	})
}

/** Mint a fresh signed URL on demand (15-min TTL).
 *
 * Pass `disposition: "attachment"` to get a URL with Content-Disposition:
 * attachment baked in — the browser will save to disk instead of previewing.
 * Default (omitted / "inline") opens the file in-tab.
 */
export function useDownloadDocument(propertyId: string) {
	return useMutation({
		mutationFn: ({
			documentId,
			disposition,
		}: {
			documentId: string
			disposition?: "inline" | "attachment"
		}) => {
			const qs = disposition === "attachment" ? "?disposition=attachment" : ""
			return apiFetch<{ document: DocumentDTO }>(
				`${documentResourcePath(propertyId, documentId)}/download${qs}`,
			).then((r) => r.document.downloadUrl as string)
		},
	})
}

/** Edit a document's title and/or aiReadable flag. */
export function useUpdateDocument(scope: DocumentScope) {
	const qc = useQueryClient()
	const propertyId = scope.propertyId
	return useMutation({
		mutationFn: (input: {
			documentId: string
			title?: string
			aiReadable?: boolean
		}) =>
			apiFetch<{ document: DocumentDTO }>(
				documentResourcePath(propertyId, input.documentId),
				{
					method: "PATCH",
					body: JSON.stringify({
						title: input.title,
						aiReadable: input.aiReadable,
					}),
				},
			),
		onSuccess: () => invalidationKeys(qc, scope),
	})
}

/**
 * Invalidate every documents-list query under a property (the property rollup
 * AND all per-project lists — both end in "documents"). Grant/revoke change
 * the lists' grantCount (ZPL-332), and the mutation only knows propertyId, so
 * a predicate beats threading DocumentScope into the grants modal.
 */
function invalidateDocumentLists(
	qc: ReturnType<typeof useQueryClient>,
	propertyId: string,
) {
	return qc.invalidateQueries({
		predicate: (q) => {
			const k = q.queryKey
			return (
				k[0] === "properties" &&
				k[1] === propertyId &&
				k[k.length - 1] === "documents"
			)
		},
	})
}

/** Grant document access to a user. */
export function useGrantDocumentAccess(propertyId: string, documentId: string) {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (userId: string) =>
			apiFetch<void>(`${documentResourcePath(propertyId, documentId)}/grants`, {
				method: "POST",
				body: JSON.stringify({ userId }),
			}),
		onSuccess: () =>
			Promise.all([
				qc.invalidateQueries({
					queryKey: queryKeys.documentGrantCandidates(propertyId, documentId),
				}),
				// The lists carry grantCount (ZPL-332) — refresh the badge.
				invalidateDocumentLists(qc, propertyId),
			]),
	})
}

/** Revoke document access from a user. */
export function useRevokeDocumentAccess(
	propertyId: string,
	documentId: string,
) {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (userId: string) =>
			apiFetch<void>(
				`${documentResourcePath(propertyId, documentId)}/grants/${userId}`,
				{ method: "DELETE" },
			),
		onSuccess: () =>
			Promise.all([
				qc.invalidateQueries({
					queryKey: queryKeys.documentGrantCandidates(propertyId, documentId),
				}),
				// The lists carry grantCount (ZPL-332) — refresh the badge.
				invalidateDocumentLists(qc, propertyId),
			]),
	})
}

/**
 * The latest AI contract review for a document (ZPL-203/209), or null if none
 * has been requested yet. Realtime lifecycle updates arrive via
 * `useDocumentReviewRealtime` (mounted once per list), which invalidates this
 * query — so a per-row badge stays current without its own subscription.
 *
 * Pass `enabled: document.canDownload` so the client never even attempts the
 * fetch for a document the viewer can't download — the review is gated on the
 * same CASL "read" tier as download (the server 403s otherwise; this keeps the
 * client honest too).
 */
export function useDocumentAiReview(
	propertyId: string,
	documentId: string,
	enabled = true,
) {
	return useQuery({
		queryKey: queryKeys.documentAiReview(propertyId, documentId),
		queryFn: () =>
			apiFetch<{ aiReview: AIResponseDTO | null }>(
				`${documentResourcePath(propertyId, documentId)}/ai-review`,
			).then((r) => r.aiReview),
		enabled: enabled && !!propertyId && !!documentId,
	})
}

/**
 * Mount once where a document list lives: subscribes to the property channel
 * and invalidates the per-document review query on every `ai-response.*` event
 * for a document, so review badges flip from "reviewing" to processed/failed in
 * realtime. One subscription covers every row (vs. one per badge).
 */
export function useDocumentReviewRealtime(propertyId: string | undefined) {
	const qc = useQueryClient()
	const onMessage = useCallback(
		(msg: RealtimeMessage) => {
			if (
				msg.event !== "ai-response.created" &&
				msg.event !== "ai-response.processed" &&
				msg.event !== "ai-response.failed"
			) {
				return
			}
			if (msg.payload.resourceType !== "document") return
			if (!propertyId) return
			qc.invalidateQueries({
				queryKey: queryKeys.documentAiReview(
					propertyId,
					msg.payload.resourceId,
				),
			})
		},
		[qc, propertyId],
	)
	usePropertyChannel(propertyId, onMessage)
}
